import React, { useState, useEffect, useRef } from 'react';
import { Bell, Info, AlertTriangle, Shield } from 'lucide-react';
import { fmt } from '../../utils/safeDate';
import { isStale } from '../../utils/staleness';
import dataService from '../../services/dataService';

// Alarms today come only from a machine's latest.alarms field (CNC monitorKind).
// Analog/digital boards in the v2 schema don't report alarms. When CNC firmware
// catches up to v2, alarms will start flowing through machines/{id}.latest.alarms.
const AlarmCenter = () => {
  const [machines, setMachines] = useState([]);
  const [machineData, setMachineData] = useState({});
  const [loading, setLoading] = useState(true);
  const subsRef = useRef([]);

  useEffect(() => {
    const unsubAll = dataService.subscribeAllMachines((data, err) => {
      if (!err && data) {
        setMachines(data);
        const seeded = {};
        data.forEach((m) => { seeded[m.id] = m; });
        setMachineData((prev) => ({ ...seeded, ...prev }));

        data.forEach((m) => {
          if (subsRef.current.find((s) => s.id === m.id)) return;
          const unsub = dataService.subscribeMachineState(m.id, (docData) => {
            if (docData) setMachineData((prev) => ({ ...prev, [m.id]: docData }));
          });
          subsRef.current.push({ id: m.id, unsub });
        });
      }
      setLoading(false);
    });
    return () => {
      try { unsubAll(); } catch (e) { /* noop */ }
      subsRef.current.forEach(({ unsub }) => { try { unsub(); } catch (e) { /* noop */ } });
      subsRef.current = [];
    };
  }, []);

  // Collect active alarms — only from live machines whose latest.alarms exists
  const activeAlarms = [];
  machines.forEach((machine) => {
    const data = machineData[machine.id] || machine;
    if (!data || isStale(data.timestamp)) return;
    const alarms = data.latest?.alarms;
    if (!Array.isArray(alarms)) return;
    alarms.forEach((alarm) => {
      activeAlarms.push({ ...alarm, machineId: machine.id, machineName: data.name || machine.id });
    });
  });

  const criticalCount = activeAlarms.filter((a) => a.severity === 'critical' || a.severity === 'error').length;
  const warningCount = activeAlarms.filter((a) => a.severity === 'warning').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="section-title">Alarm Center</h1>
        <p className="section-subtitle">Active alerts from live machines</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className={`kpi-card ${activeAlarms.length > 0 ? 'kpi-red' : 'kpi-green'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Active</p>
              <p className={`metric-lg ${activeAlarms.length > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{activeAlarms.length}</p>
            </div>
            <Bell className={`w-5 h-5 ${activeAlarms.length > 0 ? 'text-red-400' : 'text-emerald-400'} opacity-50`} />
          </div>
        </div>
        <div className="kpi-card kpi-red">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Critical</p>
              <p className="metric-lg text-red-400">{criticalCount}</p>
            </div>
            <AlertTriangle className="w-5 h-5 text-red-400 opacity-50" />
          </div>
        </div>
        <div className="kpi-card kpi-amber">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Warnings</p>
              <p className="metric-lg text-amber-400">{warningCount}</p>
            </div>
            <AlertTriangle className="w-5 h-5 text-amber-400 opacity-50" />
          </div>
        </div>
      </div>

      {activeAlarms.length > 0 ? (
        <div className="space-y-3">
          {activeAlarms.map((a, i) => (
            <div key={`${a.machineId}_${a.code || i}`} className={`card p-4 ${a.severity === 'critical' || a.severity === 'error' ? 'glow-border-error' : 'glow-border-warning'}`}>
              <div className="flex items-start gap-2.5">
                <AlertTriangle className={`w-4 h-4 mt-0.5 ${a.severity === 'critical' || a.severity === 'error' ? 'text-red-400' : 'text-amber-400'}`} />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-bold text-slate-200">{a.code || 'UNCODED'}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${a.severity === 'critical' || a.severity === 'error' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>{a.severity || 'warning'}</span>
                    <span className="text-[10px] text-slate-500">{a.machineName}</span>
                  </div>
                  {a.message && <p className="text-xs text-slate-400 mt-1">{a.message}</p>}
                  {a.timestamp && <p className="text-[10px] text-slate-600 mt-1">{fmt(a.timestamp, 'HH:mm:ss')}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-8 flex flex-col items-center justify-center text-center">
          <Shield className="w-10 h-10 text-emerald-500/60 mb-3" />
          <h3 className="text-sm font-semibold text-slate-200 mb-1">No active alarms</h3>
          <p className="text-xs text-slate-400 max-w-md">
            Alarms come from <code className="font-mono text-slate-500">latest.alarms</code> on each machine doc.
            Analog boards don't report alarms in the v2 schema. CNC alarm reporting will appear here once the CNC driver is on v2.
          </p>
        </div>
      )}

      <div className="card p-4 flex items-start gap-2.5 bg-surface-800/40">
        <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-slate-400">
          Historical alarm trends are not available — boards push only the current snapshot, no time-series.
          A resolved-alarms collection would need to be added to surface trend data.
        </p>
      </div>
    </div>
  );
};

export default AlarmCenter;
